import { fetchWithAuth } from './fetchWithAuth';
import { getMarchHttpApiBase, isMarchHttpApiConfigured } from './marchApiEnv';
import { peekSmartLaunchHandoff } from '../auth/pendingSmartLaunch';
import type {
  SmartClinicalItem,
  SmartClinicalPreview,
  SmartPatientDemographics,
  SmartPatientResource,
} from '../types/smart';

const SMART_SESSION_STORAGE_KEY = 'march_smart_session_id';
const SMART_WORKSPACE_PATIENT_STORAGE_KEY = 'march_smart_workspace_patient';
const SMART_HISTORY_WINDOW_STORAGE_KEY = 'march_smart_history_window';
const PATIENT_CONNECTION_CACHE_TTL_MS = 8 * 60 * 60 * 1000;

/**
 * How much chart history the clinician chose to load at first SMART attach
 * (Task: patient data window selection). Interactive only at the first
 * attach of a session; every later return to chat re-shows this as a
 * disabled, display-only readout of what the session already has.
 */
export type SmartHistoryWindow = '1y' | '2y' | '5y' | '10y' | 'all';

const SMART_HISTORY_WINDOW_VALUES: readonly SmartHistoryWindow[] = ['1y', '2y', '5y', '10y', 'all'];

function isSmartHistoryWindow(value: unknown): value is SmartHistoryWindow {
  return typeof value === 'string' && (SMART_HISTORY_WINDOW_VALUES as readonly string[]).includes(value);
}

/** Single source of truth for how each window code reads to a clinician —
 * the picker (SmartEntryGate) and the always-visible chat header both use
 * this, so the two can never drift to different wording for the same code. */
export const SMART_HISTORY_WINDOW_LABELS: Record<SmartHistoryWindow, string> = {
  '1y': '1 year',
  '2y': '2 years',
  '5y': '5 years',
  '10y': '10 years',
  all: 'All data',
};

/** Fired on `window` whenever the stored history window changes, so any
 * already-mounted UI (the chat header's window badge) can react without
 * polling — `sessionStorage` writes are silent to the tab that made them. */
export const SMART_HISTORY_WINDOW_CHANGED_EVENT = 'march-smart-history-window-changed';

export const SMART_SESSION_NOT_FOUND_MESSAGE =
  'Patient record connection was not found. Open the patient from the source system again, then return to MarchAgent. If this keeps happening, contact your administrator.';

function getBase(): string {
  return getMarchHttpApiBase();
}

async function safeJson<T = unknown>(res: Response): Promise<T> {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    const text = await res.text();
    throw new Error(`Patient record service returned an unexpected response (${res.status}): ${text.slice(0, 120)}`);
  }
  return (await res.json()) as T;
}

function clean(value: unknown): string {
  return String(value ?? '').trim();
}

function firstText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map((it) => clean(it)).filter(Boolean).join(' ');
  return '';
}

function getCodingDisplay(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  const obj = value as Record<string, unknown>;
  const text = clean(obj.text);
  if (text) return text;
  const coding = Array.isArray(obj.coding) ? obj.coding : [];
  for (const item of coding) {
    if (item && typeof item === 'object') {
      const display = clean((item as Record<string, unknown>).display);
      if (display) return display;
      const code = clean((item as Record<string, unknown>).code);
      if (code) return code;
    }
  }
  return '';
}

function getReferenceDisplay(value: unknown): string {
  if (!value || typeof value !== 'object') return '';
  const obj = value as Record<string, unknown>;
  return clean(obj.display) || clean(obj.reference);
}

function calculateAgeYears(birthDate: string): number | null {
  if (!birthDate) return null;
  const dob = new Date(`${birthDate}T00:00:00Z`);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const monthDiff = now.getUTCMonth() - dob.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < dob.getUTCDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

function formatName(patient: SmartPatientResource): string {
  const names = Array.isArray(patient.name) ? patient.name : [];
  const official = names.find((name) => name?.use === 'official') ?? names[0];
  if (!official) return 'Current patient';
  if (official.text?.trim()) return official.text.trim();
  const given = Array.isArray(official.given) ? official.given.join(' ') : '';
  const family = official.family ?? '';
  return `${given} ${family}`.trim() || 'Current patient';
}

function identifierByText(patient: SmartPatientResource, needle: string): string {
  const identifiers = Array.isArray(patient.identifier) ? patient.identifier : [];
  const lowerNeedle = needle.toLowerCase();
  const found = identifiers.find((item) => {
    const typeText = item?.type?.text?.toLowerCase() ?? '';
    const system = item?.system?.toLowerCase() ?? '';
    return typeText.includes(lowerNeedle) || system.includes(lowerNeedle);
  });
  return found?.value?.trim() ?? '';
}

export function isSmartModeActive(): boolean {
  if (typeof window === 'undefined') return false;
  const url = new URL(window.location.href);
  const hasDirectSession =
    clean(url.searchParams.get('session_id')) ||
    clean(url.searchParams.get('smart_session_id'));
  const smartStatus = clean(url.searchParams.get('smart')).toLowerCase();
  const stored = clean(readPatientConnectionSessionId());

  return Boolean(hasDirectSession || smartStatus === 'connected' || stored);
}

function safeSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readCachedEnvelope<T>(key: string): { value: T; savedAt: number } | null {
  const store = safeLocalStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { value?: T; savedAt?: number };
    if (!parsed.value || typeof parsed.savedAt !== 'number') {
      store.removeItem(key);
      return null;
    }
    if (Date.now() - parsed.savedAt > PATIENT_CONNECTION_CACHE_TTL_MS) {
      store.removeItem(key);
      return null;
    }
    return { value: parsed.value, savedAt: parsed.savedAt };
  } catch {
    store.removeItem(key);
    return null;
  }
}

function writeCachedEnvelope<T>(key: string, value: T): void {
  const store = safeLocalStorage();
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify({ value, savedAt: Date.now() }));
  } catch {
    // Best-effort recovery cache only.
  }
}

export function storePatientConnectionSessionId(sessionId: string): void {
  const cleanSessionId = clean(sessionId);
  if (!cleanSessionId) return;
  try {
    safeSessionStorage()?.setItem(SMART_SESSION_STORAGE_KEY, cleanSessionId);
  } catch {
    // Best-effort runtime state.
  }
  writeCachedEnvelope(SMART_SESSION_STORAGE_KEY, cleanSessionId);
}

export function readPatientConnectionSessionId(): string | null {
  // Pure read: this tab's own session first, then the cross-tab recovery
  // cache. Deliberately NO silent adoption into this tab's sessionStorage —
  // the cache holds whichever patient was launched most recently in ANY tab,
  // so adopting it must be an explicit, clinician-confirmed act
  // (adoptRecoveredPatientConnection), never a read side-effect.
  const sessionValue = clean(safeSessionStorage()?.getItem(SMART_SESSION_STORAGE_KEY));
  if (sessionValue) return sessionValue;
  const cached = readCachedEnvelope<string>(SMART_SESSION_STORAGE_KEY);
  return clean(cached?.value) || null;
}

/**
 * True when this browser has a SMART launch context to work from.
 *
 * The single question behind the clinical-workspace gate: was this session
 * started from the EHR? The clinician view exists to answer questions about a
 * patient the clinician opened in their clinical system, and without that
 * context there is no patient, no chart, and nothing the view can honestly do.
 *
 * Three sources, in the order they become true during a launch:
 *   1. the URL of a launch that has just landed,
 *   2. this tab's own session,
 *   3. the cross-tab recovery cache (a launch in a sibling tab).
 *
 * Deliberately a PURE READ -- it never adopts the recovery cache into this tab.
 * Adoption is a clinician-confirmed act (`adoptRecoveredPatientConnection`),
 * because the cache holds whichever patient was launched most recently in ANY
 * tab and silently inheriting it is a wrong-patient hazard.
 */
export function hasSmartContext(): boolean {
  try {
    if (resolveSmartSessionIdFromLocation()) return true;
  } catch {
    // A malformed URL is not a launch.
  }
  return Boolean(readPatientConnectionSessionId());
}

/**
 * Explicitly adopt a recovered (cross-tab / restored) patient connection into
 * THIS tab's sessionStorage. Only call after the clinician has confirmed the
 * patient identity (SmartEntryGate's resume screen) — this is the wrong-patient
 * safety boundary for tabs opened without an EHR launch context.
 */
export function adoptRecoveredPatientConnection(sessionId: string): void {
  const cleanSessionId = clean(sessionId);
  if (!cleanSessionId) return;
  try {
    safeSessionStorage()?.setItem(SMART_SESSION_STORAGE_KEY, cleanSessionId);
  } catch {
    // Best-effort runtime state.
  }
  const cachedPatient = readCachedEnvelope<Partial<ReturnType<typeof smartPatientToWorkspacePatient>>>(
    SMART_WORKSPACE_PATIENT_STORAGE_KEY,
  );
  if (cachedPatient?.value) {
    try {
      safeSessionStorage()?.setItem(SMART_WORKSPACE_PATIENT_STORAGE_KEY, JSON.stringify(cachedPatient.value));
    } catch {
      // Best-effort UI state only.
    }
  }
}

/**
 * Ask the server to evict the SMART session capability (logout, wrong-patient
 * escape, context switch). Best-effort: a network/HTTP failure must never block
 * the local teardown, so the caller still clears local state afterwards.
 */
export async function closeSmartSession(sessionId: string): Promise<void> {
  const cleanSessionId = clean(sessionId);
  if (!cleanSessionId || !isMarchHttpApiConfigured()) return;
  try {
    await fetchWithAuth(`${getBase()}/api/smart/session/close`, {
      method: 'POST',
      extraHeaders: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: cleanSessionId }),
      skip401Retry: true,
      skipStaleAccessHandling: true,
    });
  } catch {
    // Best-effort server eviction; the session also expires on its own TTL.
  }
}

/**
 * Full patient-connection teardown: evict the server session (best-effort),
 * then clear all local patient state. Use for logout and wrong-patient escapes.
 */
export async function closePatientConnection(): Promise<void> {
  const sessionId = readPatientConnectionSessionId();
  if (sessionId) {
    await closeSmartSession(sessionId);
  }
  clearPatientConnectionState();
}

export function clearPatientConnectionState(): void {
  try {
    safeSessionStorage()?.removeItem(SMART_SESSION_STORAGE_KEY);
    safeSessionStorage()?.removeItem(SMART_WORKSPACE_PATIENT_STORAGE_KEY);
    safeSessionStorage()?.removeItem(SMART_HISTORY_WINDOW_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
  try {
    safeLocalStorage()?.removeItem(SMART_SESSION_STORAGE_KEY);
    safeLocalStorage()?.removeItem(SMART_WORKSPACE_PATIENT_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

export interface SmartSessionResolution {
  sessionId: string;
  /**
   * Where the session binding came from:
   * - `launch`    — explicit EHR launch params on this tab's URL (authoritative);
   * - `tab`       — this tab's own sessionStorage (same patient this tab already had);
   * - `recovered` — the cross-tab localStorage recovery cache. This is whichever
   *                 patient was launched most recently in ANY tab, so it MUST be
   *                 confirmed by the clinician before use (wrong-patient risk).
   */
  source: 'launch' | 'tab' | 'recovered';
}

export function resolveSmartSessionFromLocationDetailed(): SmartSessionResolution | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const direct = clean(url.searchParams.get('session_id')) || clean(url.searchParams.get('smart_session_id'));
  if (direct) {
    storePatientConnectionSessionId(direct);
    return { sessionId: direct, source: 'launch' };
  }
  const tabValue = clean(safeSessionStorage()?.getItem(SMART_SESSION_STORAGE_KEY));
  if (tabValue) {
    // A launch that lost its URL still counts as a launch. `WorkspaceRedirect`
    // sends `/` to the default panel with `<Navigate replace>`, which drops the
    // query string, so a genuine EHR launch reaches this point looking exactly
    // like a same-tab resume. The handoff marker, written when the params were
    // still readable, is what tells the two apart.
    //
    // The marker must MATCH this session id: a leftover marker from an earlier
    // launch (or another clinician on a shared workstation) must never unlock
    // the window picker for a session it did not come from.
    if (peekSmartLaunchHandoff() === tabValue) {
      return { sessionId: tabValue, source: 'launch' };
    }
    return { sessionId: tabValue, source: 'tab' };
  }
  const cached = clean(readCachedEnvelope<string>(SMART_SESSION_STORAGE_KEY)?.value);
  if (cached) {
    return { sessionId: cached, source: 'recovered' };
  }
  return null;
}

export function resolveSmartSessionIdFromLocation(): string | null {
  return resolveSmartSessionFromLocationDetailed()?.sessionId ?? null;
}

export function smartPatientToWorkspacePatient(patient: SmartPatientDemographics): {
  name: string;
  mrn: string;
  status: string;
  risk: string;
  source: 'smart-on-fhir';
} {
  return {
    name: patient.displayName || 'Current patient',
    mrn: patient.fhirId || patient.mrn || patient.accountNumber,
    status: patient.active === false ? 'Inactive' : 'Active',
    risk: 'Connected',
    source: 'smart-on-fhir',
  };
}

export function storeSmartWorkspacePatient(patient: SmartPatientDemographics): void {
  if (typeof window === 'undefined') return;
  const workspacePatient = smartPatientToWorkspacePatient(patient);
  try {
    safeSessionStorage()?.setItem(SMART_WORKSPACE_PATIENT_STORAGE_KEY, JSON.stringify(workspacePatient));
  } catch {
    // Best-effort UI state only.
  }
  writeCachedEnvelope(SMART_WORKSPACE_PATIENT_STORAGE_KEY, workspacePatient);
}

export function getStoredSmartWorkspacePatient(): ReturnType<typeof smartPatientToWorkspacePatient> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = safeSessionStorage()?.getItem(SMART_WORKSPACE_PATIENT_STORAGE_KEY);
    const cached = raw
      ? { value: JSON.parse(raw) as Partial<ReturnType<typeof smartPatientToWorkspacePatient>> }
      : readCachedEnvelope<Partial<ReturnType<typeof smartPatientToWorkspacePatient>>>(SMART_WORKSPACE_PATIENT_STORAGE_KEY);
    if (!cached?.value) return null;
    const parsed = cached.value;
    const mrn = clean(parsed.mrn);
    const name = clean(parsed.name) || 'Current patient';
    if (!mrn) return null;
    // Pure read — no write-back into sessionStorage. Adoption of a recovered
    // (cross-tab) patient happens only via adoptRecoveredPatientConnection
    // after explicit clinician confirmation.
    return {
      name,
      mrn,
      status: clean(parsed.status) || 'Active',
      risk: clean(parsed.risk) || 'Connected',
      source: 'smart-on-fhir' as const,
    };
  } catch {
    return null;
  }
}

export async function smartApiErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const body = (await res.json()) as { detail?: unknown; message?: unknown; error?: unknown };
      const detail = clean(body.detail || body.message || body.error);
      if (detail === 'SMART session not found') return SMART_SESSION_NOT_FOUND_MESSAGE;
      if (detail) return detail.slice(0, 240);
    } else {
      const text = clean(await res.text());
      if (text === 'SMART session not found' || text.includes('SMART session not found')) {
        return SMART_SESSION_NOT_FOUND_MESSAGE;
      }
    }
  } catch {
    // Fall back below.
  }
  return fallback;
}

export async function fetchSmartPatient(sessionId: string): Promise<SmartPatientResource> {
  if (!isMarchHttpApiConfigured()) throw new Error('March API URL is not configured.');
  // Route through fetchWithAuth: the server binds this SMART session to the
  // authenticated clinician (owner-of-first-read) and rejects any other user,
  // so the session_id can no longer be replayed as an unbound PHI capability.
  const res = await fetchWithAuth(`${getBase()}/api/smart/fhir/patient`, {
    method: 'POST',
    extraHeaders: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
    // A SMART owner-mismatch 403 is scoped to THIS patient session — surface it
    // locally in the gate; never let it trigger the app-wide access-denied
    // redirect, which would evict a clinician who still has valid app access.
    skipStaleAccessHandling: true,
  });
  if (!res.ok) {
    throw new Error(await smartApiErrorMessage(res, `Failed to load patient record (${res.status})`));
  }
  return await safeJson<SmartPatientResource>(res);
}

export async function fetchSmartImportPreview(sessionId: string, window?: SmartHistoryWindow): Promise<unknown> {
  if (!isMarchHttpApiConfigured()) throw new Error('March API URL is not configured.');
  const body: { session_id: string; window?: SmartHistoryWindow } = { session_id: sessionId };
  // Send the window whenever the caller has one — including on a re-attach,
  // where it is the window the session already has.
  //
  // This used to be omitted on every re-attach, to keep that request shaped
  // exactly as it was before the picker existed. That turned out to be the bug:
  // the server reads a missing window as "load everything", so a resume that
  // re-imported silently widened the chart to its full history while the screen
  // still displayed "2 years" (2026-07-31). Re-sending the same value is
  // idempotent; omitting it is a change of meaning.
  if (window) body.window = window;
  const res = await fetchWithAuth(`${getBase()}/api/smart/import/preview`, {
    method: 'POST',
    extraHeaders: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    // Patient-session-scoped 403 — surface locally, not app-wide (see fetchSmartPatient).
    skipStaleAccessHandling: true,
  });
  if (!res.ok) {
    throw new Error(await smartApiErrorMessage(res, `Failed to load clinical summary (${res.status})`));
  }
  return await safeJson<unknown>(res);
}

/**
 * Which stage of loading this session's chart is in.
 *
 * `unknown` means the question could not be answered — the request failed, or
 * came back unreadable. It is deliberately NOT a synonym for ready: treating a
 * failed check as "the chart is loaded" is what let a single transient error
 * drop a clinician into a chat whose chart was still importing.
 */
export type SmartChartPhase = 'importing' | 'building' | 'ready' | 'unknown';

export interface SmartChartStatus {
  /** True when a chat question would actually find the chart. */
  ready: boolean;
  phase: SmartChartPhase;
  /** Rows per clinical table once built — used to show real progress. */
  resourceCounts: Record<string, number>;
  /** The history window the session actually applied, per the server. */
  window: SmartHistoryWindow | null;
  /** Short human-readable reason, safe to show. */
  detail: string;
}

const CHART_STATUS_UNKNOWN: SmartChartStatus = {
  ready: false,
  phase: 'unknown',
  resourceCounts: {},
  window: null,
  detail: 'Could not check whether the chart is loaded.',
};

/**
 * Ask whether this session's chart can answer a question yet.
 *
 * Never throws. A request that fails reports `unknown` — NOT ready. The caller
 * decides how many consecutive failures justify giving up; a single one never
 * does. (Before 2026-07-31 this returned ready on any error, so one transient
 * 401 opened the chat onto a chart that was minutes away from being loaded.)
 */
export async function fetchSmartChartStatus(
  sessionId: string,
  expectedWindow?: SmartHistoryWindow,
): Promise<SmartChartStatus> {
  if (!isMarchHttpApiConfigured()) return CHART_STATUS_UNKNOWN;
  try {
    const body: { session_id: string; window?: SmartHistoryWindow } = { session_id: sessionId };
    if (expectedWindow) body.window = expectedWindow;
    const res = await fetchWithAuth(`${getBase()}/api/smart/session/chart-status`, {
      method: 'POST',
      extraHeaders: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      // Patient-session-scoped 403 — surface locally, not app-wide (see fetchSmartPatient).
      skipStaleAccessHandling: true,
    });
    if (!res.ok) return CHART_STATUS_UNKNOWN;
    const raw = (await safeJson<Record<string, unknown>>(res)) as Record<string, unknown> | null;
    if (!raw || typeof raw !== 'object') return CHART_STATUS_UNKNOWN;
    const phase = raw.phase;
    const counts = raw.resource_counts;
    return {
      ready: raw.ready === true,
      phase: phase === 'importing' || phase === 'building' || phase === 'ready' ? phase : 'importing',
      resourceCounts:
        counts && typeof counts === 'object' ? (counts as Record<string, number>) : {},
      window: isSmartHistoryWindow(raw.window) ? raw.window : null,
      detail: typeof raw.detail === 'string' ? raw.detail : '',
    };
  } catch {
    return CHART_STATUS_UNKNOWN;
  }
}

/** How often the entry screen re-asks whether the chart is ready. */
export const CHART_READY_POLL_MS = 2000;
/** After this long, the entry screen offers a way through rather than only waiting. */
export const CHART_READY_SLOW_AFTER_MS = 45000;
/** Hard ceiling — past this the clinician is let into the chat regardless. */
export const CHART_READY_GIVE_UP_MS = 10 * 60 * 1000;
/**
 * Consecutive failed checks before the wait gives up and opens the chat.
 *
 * Never 1. A single failure is a blip — a token refresh landing mid-flight, an
 * edge 502, a dropped connection — and the chart is almost certainly still
 * loading normally behind it. Giving up on the first one turns a two-second
 * hiccup into "here is a chart that is not loaded yet", which is precisely the
 * failure this whole wait exists to prevent.
 */
export const CHART_READY_MAX_CONSECUTIVE_ERRORS = 5;

/**
 * Wait until this session's chart is queryable, reporting progress as it goes.
 *
 * Resolves when the chart is ready, and ALSO resolves (never rejects) when the
 * ceiling is hit or the checks keep failing — the caller then opens the chat
 * anyway. A clinician must always have a way forward; entering a still-loading
 * chat is the floor this degrades to, not a dead end.
 *
 * `expectedWindow` is passed to the server so a chart left over from an earlier
 * attach, built under a different history window, does not satisfy this wait.
 */
export async function waitForSessionChartReady(
  sessionId: string,
  options: {
    onUpdate?: (status: SmartChartStatus, elapsedMs: number) => void;
    shouldStop?: () => boolean;
    expectedWindow?: SmartHistoryWindow;
    pollMs?: number;
    giveUpMs?: number;
    maxConsecutiveErrors?: number;
  } = {},
): Promise<SmartChartStatus> {
  const pollMs = options.pollMs ?? CHART_READY_POLL_MS;
  const giveUpMs = options.giveUpMs ?? CHART_READY_GIVE_UP_MS;
  const maxErrors = options.maxConsecutiveErrors ?? CHART_READY_MAX_CONSECUTIVE_ERRORS;
  const startedAt = Date.now();
  let last: SmartChartStatus = CHART_STATUS_UNKNOWN;
  let consecutiveErrors = 0;

  for (;;) {
    if (options.shouldStop?.()) return last;
    last = await fetchSmartChartStatus(sessionId, options.expectedWindow);
    const elapsed = Date.now() - startedAt;
    options.onUpdate?.(last, elapsed);

    if (last.ready) return last;

    if (last.phase === 'unknown') {
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxErrors) {
        // The check itself is broken, not the chart. Let the clinician in
        // rather than hold them on a screen that can no longer learn anything.
        return { ...last, ready: true };
      }
    } else {
      // A real answer clears the streak: intermittent failures around a chart
      // that IS progressing must not accumulate into a false give-up.
      consecutiveErrors = 0;
    }

    if (elapsed >= giveUpMs) return { ...last, ready: true };
    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }
}

/**
 * Reads back the history window the import/preview response says it actually
 * applied — the server's answer, which outranks anything this tab remembers.
 *
 * The server sends it as `data_window`. This function only looked for `window`,
 * so the echo never resolved and the locked readout always fell back to the
 * local copy — which is how a session could show "2 years" while the backend
 * had loaded the full chart (2026-07-31). Both keys are accepted now:
 * `data_window` first because that is what the server sends.
 */
export function extractEchoedHistoryWindow(preview: unknown): SmartHistoryWindow | null {
  if (!preview || typeof preview !== 'object') return null;
  const record = preview as Record<string, unknown>;
  const value = record.data_window ?? record.window;
  return isSmartHistoryWindow(value) ? value : null;
}

/**
 * Display-only persistence of the chart-history window for THIS tab's
 * session — never read back to pre-select a default on a fresh launch
 * (that default is always "2 years"); only used to show, disabled, what an
 * already-active session's window is when the clinician returns to chat.
 */
export function storeSmartHistoryWindow(historyWindow: SmartHistoryWindow): void {
  try {
    safeSessionStorage()?.setItem(SMART_HISTORY_WINDOW_STORAGE_KEY, historyWindow);
  } catch {
    // Best-effort display state only.
  }
  try {
    // sessionStorage writes never notify the tab that made them, so anything
    // already mounted (the chat header's window badge) needs an explicit
    // signal to pick up a change without polling.
    window.dispatchEvent(
      new CustomEvent(SMART_HISTORY_WINDOW_CHANGED_EVENT, { detail: historyWindow }),
    );
  } catch {
    // Best-effort UI notification only.
  }
}

export function getStoredSmartHistoryWindow(): SmartHistoryWindow | null {
  const raw = safeSessionStorage()?.getItem(SMART_HISTORY_WINDOW_STORAGE_KEY);
  return isSmartHistoryWindow(raw) ? raw : null;
}

export function normalizeSmartPatient(patient: SmartPatientResource): SmartPatientDemographics {
  const phone = (patient.telecom ?? []).find((item) => item.system === 'phone')?.value ?? '';
  const email = (patient.telecom ?? []).find((item) => item.system === 'email')?.value ?? '';
  const address = patient.address?.[0];
  const addressText = address
    ? [firstText(address.line), address.city, address.state, address.postalCode, address.country]
        .map(clean)
        .filter(Boolean)
        .join(', ')
    : '';
  const birthDate = patient.birthDate ?? '';
  return {
    fhirId: patient.id ?? '',
    displayName: formatName(patient),
    mrn: identifierByText(patient, 'mrn'),
    accountNumber: identifierByText(patient, 'account'),
    gender: patient.gender ?? '',
    birthDate,
    ageYears: calculateAgeYears(birthDate),
    active: typeof patient.active === 'boolean' ? patient.active : null,
    phone,
    email,
    address: addressText,
    managingOrganization: patient.managingOrganization?.display ?? patient.managingOrganization?.reference ?? '',
  };
}

function bundleEntries(bundle: unknown): Record<string, unknown>[] {
  if (!bundle || typeof bundle !== 'object') return [];
  const entries = (bundle as Record<string, unknown>).entry;
  if (!Array.isArray(entries)) return [];
  return entries
    .map((entry) => (entry && typeof entry === 'object' ? (entry as Record<string, unknown>).resource : null))
    .filter((resource): resource is Record<string, unknown> => !!resource && typeof resource === 'object');
}

function resourceTitle(resource: Record<string, unknown>): string {
  return (
    getCodingDisplay(resource.code) ||
    getCodingDisplay(resource.type) ||
    getCodingDisplay(resource.category) ||
    getCodingDisplay(resource.vaccineCode) ||
    getCodingDisplay(resource.medicationCodeableConcept) ||
    getCodingDisplay(resource.reasonCode) ||
    clean(resource.status) ||
    clean(resource.id) ||
    'Clinical resource'
  );
}

function resourceDate(resource: Record<string, unknown>): string {
  const period = resource.period && typeof resource.period === 'object' ? (resource.period as Record<string, unknown>) : null;
  return (
    clean(resource.effectiveDateTime) ||
    clean(resource.recordedDate) ||
    clean(resource.authoredOn) ||
    clean(resource.date) ||
    clean(resource.performedDateTime) ||
    clean(resource.onsetDateTime) ||
    clean(period?.start) ||
    clean(period?.end)
  );
}

function resourceTime(resource: Record<string, unknown>): number {
  const raw = resourceDate(resource);
  if (!raw) return 0;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function clinicalItem(resource: Record<string, unknown>): SmartClinicalItem {
  const title = resourceTitle(resource);
  const subtitle =
    getReferenceDisplay(resource.encounter) ||
    getReferenceDisplay(resource.subject) ||
    getCodingDisplay(resource.reasonCode) ||
    getCodingDisplay(resource.serviceType);
  return {
    id: clean(resource.id) || title,
    title,
    subtitle: subtitle || undefined,
    date: resourceDate(resource) || undefined,
    status: clean(resource.status) || getCodingDisplay(resource.clinicalStatus) || undefined,
    category: getCodingDisplay(resource.category) || undefined,
  };
}

function resourcesFromPreview(preview: unknown, resourceType: string): Record<string, unknown>[] {
  if (!preview || typeof preview !== 'object') return [];
  const search = (preview as Record<string, unknown>).resource_search;
  if (!search || typeof search !== 'object') return [];
  const resourceBlock = (search as Record<string, unknown>)[resourceType];
  if (!resourceBlock || typeof resourceBlock !== 'object') return [];
  const bundles = (resourceBlock as Record<string, unknown>).bundles;
  if (!Array.isArray(bundles)) return [];
  return bundles.flatMap(bundleEntries).filter((resource) => resource.resourceType === resourceType);
}

function countFromPreview(preview: unknown, resourceType: string): number {
  if (!preview || typeof preview !== 'object') return 0;
  const search = (preview as Record<string, unknown>).resource_search;
  if (!search || typeof search !== 'object') return 0;
  const resourceBlock = (search as Record<string, unknown>)[resourceType];
  if (!resourceBlock || typeof resourceBlock !== 'object') return 0;
  const count = Number((resourceBlock as Record<string, unknown>).count ?? 0);
  return Number.isFinite(count) ? count : 0;
}

function topItems(preview: unknown, resourceType: string, limit = 8): SmartClinicalItem[] {
  return resourcesFromPreview(preview, resourceType)
    .sort((a, b) => resourceTime(b) - resourceTime(a))
    .slice(0, limit)
    .map(clinicalItem);
}

export function normalizeSmartClinicalPreview(preview: unknown): SmartClinicalPreview {
  const resourceTypes = [
    'Encounter',
    'Condition',
    'Observation',
    'MedicationRequest',
    'MedicationStatement',
    'AllergyIntolerance',
    'Procedure',
    'DiagnosticReport',
    'DocumentReference',
    'CarePlan',
    'Immunization',
  ];
  const strategy = preview && typeof preview === 'object' ? clean((preview as Record<string, unknown>).strategy) : '';
  return {
    strategy: strategy || undefined,
    counts: resourceTypes
      .map((resourceType) => ({ resourceType, count: countFromPreview(preview, resourceType) }))
      .filter((item) => item.count > 0),
    conditions: topItems(preview, 'Condition'),
    observations: topItems(preview, 'Observation'),
    encounters: topItems(preview, 'Encounter'),
    medications: [
      ...topItems(preview, 'MedicationRequest', 4),
      ...topItems(preview, 'MedicationStatement', 4),
    ].slice(0, 8),
    allergies: topItems(preview, 'AllergyIntolerance'),
    procedures: topItems(preview, 'Procedure'),
    reports: topItems(preview, 'DiagnosticReport'),
    documents: topItems(preview, 'DocumentReference'),
    carePlans: topItems(preview, 'CarePlan'),
    immunizations: topItems(preview, 'Immunization'),
  };
}
